import { useState, useEffect, useCallback } from 'react'
import { TrashIcon, DocumentTextIcon, ArrowPathIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { vendorDocumentsAPI, VendorDocument } from '../../lib/services'

interface VendorDocumentListProps {
  vendorId: number
  productId?: number
  onSpecsExtracted?: () => void
  refreshTrigger?: number
}

export default function VendorDocumentList({ vendorId, productId, onSpecsExtracted, refreshTrigger }: VendorDocumentListProps) {
  const [documents, setDocuments] = useState<VendorDocument[]>([])
  const [loading, setLoading] = useState(true)

  const fetchDocuments = useCallback(async () => {
    try {
      const docs = await vendorDocumentsAPI.list(vendorId, productId)
      setDocuments(docs)
    } catch (err) {
      console.error('Failed to load documents:', err)
    } finally {
      setLoading(false)
    }
  }, [vendorId, productId])

  useEffect(() => {
    fetchDocuments()
  }, [fetchDocuments, refreshTrigger])

  // Poll for any documents still processing
  useEffect(() => {
    const processing = documents.filter(
      d => d.processing_status === 'uploading' || d.processing_status === 'processing' || d.processing_status === 'extracting_specs'
    )
    if (processing.length === 0) return

    const interval = setInterval(async () => {
      const updated = await vendorDocumentsAPI.list(vendorId, productId)
      setDocuments(updated)

      // Check if any previously-processing docs are now ready
      const nowReady = updated.filter(
        u => u.processing_status === 'ready' && processing.some(p => p.id === u.id)
      )
      if (nowReady.length > 0 && onSpecsExtracted) {
        onSpecsExtracted()
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [documents, vendorId, productId, onSpecsExtracted])

  const handleDelete = async (docId: number) => {
    try {
      await vendorDocumentsAPI.delete(docId)
      setDocuments(prev => prev.filter(d => d.id !== docId))
    } catch (err) {
      console.error('Failed to delete document:', err)
    }
  }

  if (loading) {
    return <div className="text-xs text-muted-foreground py-2">Loading documents...</div>
  }

  if (documents.length === 0) {
    return null
  }

  return (
    <div className="mt-3 space-y-1.5">
      {documents.map(doc => (
        <div
          key={doc.id}
          className="flex items-center gap-2 p-2 rounded border border-border/50 bg-muted/30 group"
        >
          <DocumentTextIcon className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-foreground truncate">{doc.filename}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-muted-foreground">
                {doc.document_type.replace('_', ' ')}
              </span>
              {doc.processing_status === 'ready' && doc.observations_created > 0 && (
                <span className="text-[10px] text-green-600">
                  {doc.observations_created} specs extracted
                </span>
              )}
              {doc.product_names && doc.product_names.length > 0 && (
                <span className="text-[10px] text-blue-500">
                  {doc.product_names.map((p, i) => {
                    const isNew = doc.product_association_results?.new_products_created?.some(
                      (np: { id: number }) => np.id === p.id
                    )
                    return (
                      <span key={p.id}>
                        {i > 0 && ', '}
                        {p.name}
                        {isNew && <span className="text-amber-500 ml-0.5">(new)</span>}
                      </span>
                    )
                  })}
                </span>
              )}
            </div>
          </div>
          <StatusBadge status={doc.processing_status} error={doc.processing_error} />
          <button
            onClick={() => handleDelete(doc.id)}
            className="p-0.5 hover:bg-muted rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <TrashIcon className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      ))}
    </div>
  )
}

function StatusBadge({ status, error }: { status: string; error?: string }) {
  switch (status) {
    case 'uploading':
    case 'processing':
      return <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin shrink-0" />
    case 'extracting_specs':
      return (
        <span className="text-[10px] text-amber-500 flex items-center gap-1 shrink-0">
          <ArrowPathIcon className="w-3 h-3 animate-spin" />
          Extracting
        </span>
      )
    case 'ready':
      return <CheckCircleIcon className="w-4 h-4 text-green-500 shrink-0" />
    case 'error':
      return (
        <span title={error || 'Processing failed'}>
          <ExclamationCircleIcon className="w-4 h-4 text-red-500 shrink-0" />
        </span>
      )
    default:
      return null
  }
}

// Export addDocument helper for parent components to use via ref
export type { VendorDocumentListProps }
